
import { defineComponent, computed, ref, toRefs, PropType, watch } from "vue";
import AdmIcon from "../Icon";
import { useId } from "../hooks/identifier";
import "./select.scss";

export default defineComponent({
  name: "adm-select",
  inheritAttrs: false,
  components: {
    AdmIcon,
  },
  props: {
    id: {
      type: String,
      default: "",
      required: true,
    },
    forceId: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
      default: "",
    },
    options: {
      type: Array as PropType<
        Array<{ label: string; value: string; disabled?: boolean }>
      >,
      required: true,
    },
    label: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: "",
    },
    error: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const {
      modelValue,
      options,
      disabled,
      label,
      placeholder,
      description,
      error,
    } = toRefs(props);
    const identifier = useId(props, "adm-select");
    const value = ref<string>(modelValue.value);

    watch(value, (nextValue) => {
      emit("update:modelValue", nextValue);
    });

    watch(modelValue, (nextModelValue) => {
      if (nextModelValue !== value.value) {
        value.value = nextModelValue;
      }
    });

    const hasValue = computed(() =>
      value.value
        ? options.value.some((option) => option.value === value.value)
        : false
    );

    const hasLabel = computed(
      () =>
        (label.value && hasValue.value) ||
        (label.value && !hasValue.value && !placeholder.value)
    );

    const hasPlaceholder = computed(() => placeholder.value && !hasValue.value);

    const classes = computed(() => [
      "adm-select",
      {
        "adm-select--disabled": disabled.value,
        "adm-select--selected": hasValue.value,
        "adm-select--no-label": !label.value,
        "adm-select--trailing-text": !!description.value || !!error.value,
        "adm-select--error": !!error.value,
      },
    ]);

    return {
      identifier,
      classes,
      value,
      hasLabel,
      hasPlaceholder,
    };
  },
});
